// Designer-side CaptureRuntime: fills the shared capture interfaces'
// injection contract (@twasta/page-runtime/txn/capture) with the platform's
// embedDataApi adapter (site-mode vs author-only routing), the designer
// TxnRuntime (live-preview endpoints + document store + react-hot-toast)
// and the designer layout persistence — preserving exactly how the capture
// views behaved before Phase 3b moved them into the shared package.

import type { CaptureRuntime, EmbedDataApi } from '@twasta/page-runtime/txn/capture'
import { saveTransactionObject } from '@/api/designers'
import { modelOverlayPatch } from './api'
import {
  embedExecute, embedExecuteBatch, embedList, embedLoadModel,
  embedResolveDefaults, embedRunAction, makeEmbedUploader,
} from './embedDataApi'
import { makeDesignerTxnRuntime } from './runtime/designerTxnRuntime'

// The platform's embedDataApi module IS the adapter the shared contract
// mirrors — bind its functions once (module-stable, like the old direct
// imports, so hook callback identities don't churn).
export const designerEmbedDataApi: EmbedDataApi = {
  loadModel: embedLoadModel,
  list: embedList,
  resolveDefaults: embedResolveDefaults,
  execute: embedExecute,
  executeBatch: embedExecuteBatch,
  runAction: embedRunAction,
  makeUploader: makeEmbedUploader,
}

export function makeDesignerCaptureRuntime(
  project: string,
  // Host deploy hook — each capture shim passes its own module-scope
  // idempotent live-preview deploy (mirroring the pre-move trackers).
  ensureReady?: (project: string, objName: string) => Promise<void>,
  // Layout persistence mode, mirroring TxnScreen's gridLayoutMode: 'design' for
  // the object designer (bakes into the shared core design), 'customize' for
  // the 7077 Run view (per-tenant overlay — never mutates the shared design).
  layoutMode: 'design' | 'customize' = 'design',
): CaptureRuntime {
  return {
    api: designerEmbedDataApi,
    txn: makeDesignerTxnRuntime(project),
    // Same persistence the platform useGridLayout wrapper curries in:
    // "Apply layout" bakes into the design JSON; customize mode writes a
    // per-tenant model overlay.
    layout: {
      saveDesign: (proj, objName, model) =>
        saveTransactionObject(proj, objName, { transaction: model }),
      saveOverlay: (proj, objName, set) =>
        modelOverlayPatch(proj, objName, set, [], 'T'),
    },
    layoutMode,
    ensureReady,
  }
}
